// /Users/donovan68/dva-frontend-vite/src/layouts/AppShell.jsx
// ============================================================
// UPDATED: Uses useDemoPlan hook for demo mode support
// UPDATED: Added hideSecondaryNav prop to hide secondary nav for EA routes
// UPDATED: EA routes get simplified header without Home/Tools/Documents/Messages
// ============================================================

import React from 'react';
import { useLocation } from 'react-router-dom';
import AppHeader from '../components/header/AppHeader';
import NotificationsModal from '../components/NotificationsModal';
import { useClientProfile } from '../hooks/useClientProfile';
import { useFilter } from '../contexts/FilterContext';
import { usePageContext } from '../contexts/PageContext';
import { useDemoPlan } from '../hooks/useDemoPlan';
import { fetchUnresolvedNotificationCount } from '../services/adminNotificationService';

export default function AppShell({ children, hideSecondaryNav = false }) {
  const location = useLocation();
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);

  // Shared filters for tabs
  const { activeFilter, setActiveFilter } = useFilter();

  // Page context set by pages (headline, title, breadcrumbs, tabs)
  const { page } = usePageContext();

  // Client profile for user data (organization name, etc.)
  const { data, isLoading, error } = useClientProfile();

  // Get plan from demo hook (respects demo mode override)
  const { plan, isPro } = useDemoPlan();

  // Notification badge count — from admin intake API unresolved_count (tenant 300 for now)
  const [messageCount, setMessageCount] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;

    async function loadBadgeCount() {
      try {
        const count = await fetchUnresolvedNotificationCount();
        if (!cancelled) setMessageCount(count);
      } catch (err) {
        console.error('Failed to load notification badge count:', err);
        if (!cancelled) setMessageCount(0);
      }
    }

    loadBadgeCount();

    return () => {
      cancelled = true;
    };
  }, []);

  // Check if we're on an EA route
  const isEARoute = location.pathname.startsWith('/ea');

  console.log('🔵 AppShell rendering with plan:', plan, 'activeFilter:', activeFilter, 'isEARoute:', isEARoute);
  
  // Determine if we're on a Pro journey route (should show prospect panel)
  const isProJourneyRoute = React.useMemo(() => {
    const path = location.pathname;
    
    // Pro journey routes where prospect panel should appear
    const proRoutes = [
      '/home',
      '/pro/analysis',
      '/pro/roadmap-planning',
      '/pro/approval',
      '/pro/execution',
    ];
    
    if (proRoutes.includes(path)) {
      return plan === 'pro';
    }
    
    if (path.startsWith('/pro/analysis/') || path.startsWith('/pro/roadmap-planning/')) {
      return plan === 'pro';
    }
    
    return false;
  }, [location.pathname, plan]);

  // Determine if we're on a Freemium journey route
  const isFreemiumJourneyRoute = React.useMemo(() => {
    const path = location.pathname;
    
    const freemiumRoutes = [
      '/home',
      '/personalize',
      '/freemium/strategy',
      '/freemium/upgrade',
    ];
    
    if (freemiumRoutes.includes(path)) {
      return plan === 'freemium';
    }
    
    if (path === '/home' || path === '/') {
      return plan === 'freemium';
    }
    
    return false;
  }, [location.pathname, plan]);
  
  console.log('🎯 AppShell - isProJourneyRoute:', isProJourneyRoute, 'path:', location.pathname);
  
  // User info from profile - show plan indicator for demos
  // Only add suffix if it's not already there
  const baseName = data?.organization_name || data?.client_name || 'User';
  const hasPlanSuffix = baseName.includes('(PRO)') || baseName.includes('(FREE)');
  const planSuffix = hasPlanSuffix ? '' : (plan === 'pro' ? ' (PRO)' : '');
  const user = { 
    name: baseName + planSuffix,
    avatarUrl: undefined 
  };

  const handleSearch = (query) => {
    console.log('Search query:', query);
  };

  const handleTabChange = (tab) => {
    console.log('🔑 Tab clicked:', tab);
    console.log('🔑 Current activeFilter:', activeFilter);
    
    if (tab === 'Messages') {
      setNotificationsOpen(true);
      return;
    }

    if (tab === 'Tools' || tab === 'Documents') {
      setActiveFilter(tab);
      console.log('🔑 Set activeFilter to:', tab);
    } else {
      setActiveFilter('Tools');
      console.log('🔑 Set activeFilter to: Tools (default)');
    }
  };

  // Route fallback when a page hasn't set PageContext yet
  const routeFallback = (() => {
    const path = location.pathname;
    
    // EA routes - always return empty breadcrumbs
    if (path.startsWith('/ea')) {
      return { 
        title: 'EA Prototype',
        breadcrumbs: []
      };
    }
    
    // Freemium routes
    if (path.includes('/freemium/personalize')) {
      return { 
        title: 'Personalize Your Experience',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Personalize' }
        ]
      };
    }
    if (path.includes('/freemium/strategy')) {
      return { 
        title: 'View Industry Insights',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Industry Insights' }
        ]
      };
    }
    if (path.includes('/freemium/upgrade')) {
      return { 
        title: 'Upgrade to DVA Pro',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Upgrade' }
        ]
      };
    }
    
    // Pro Journey routes - return empty breadcrumbs to hide the white band
    if (path === '/home' || path === '/') {
      return { 
        title: 'View Industry Insights',
        breadcrumbs: []
      };
    }
    if (path === '/pro/analysis' || path.startsWith('/pro/analysis/')) {
      return {
        title: 'Analysis & Qualification',
        breadcrumbs: []
      };
    }
    if (path === '/pro/roadmap-planning') {
      return {
        title: 'Roadmap Planning',
        breadcrumbs: []
      };
    }
    if (path === '/pro/approval') {
      return {
        title: 'Approval & Governance',
        breadcrumbs: []
      };
    }
    if (path === '/pro/execution') {
      return {
        title: 'Execution & Tracking',
        breadcrumbs: []
      };
    }
    
    // Other Pro routes
    if (path.includes('/pro/personalize') || path === '/personalize') {
      return { 
        title: 'Personalization',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Personalize' }
        ]
      };
    }
    if (path.includes('/pro/roi')) {
      return { 
        title: 'ROI Modeling',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'ROI' }
        ]
      };
    }
    if (path.includes('/pro/roadmap') && path !== '/pro/roadmap-planning') {
      return { 
        title: 'Implementation Roadmap',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Roadmap' }
        ]
      };
    }
    
    // Common routes
    if (path.includes('/matrix')) {
      return { 
        title: 'Use Case Matrix',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Matrix' }
        ]
      };
    }
    if (path.includes('/results')) {
      return { 
        title: 'Results & Analysis',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Results' }
        ]
      };
    }
    if (path.includes('/settings')) {
      return { 
        title: 'Settings',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Settings' }
        ]
      };
    }
    
    return { 
      title: 'View Industry Insights',
      breadcrumbs: []
    };
  })();

  const headline = page.headline || '';
  const contextTitle = page.title || routeFallback.title;
  
  let breadcrumbs;
  if (page.breadcrumbs !== undefined) {
    breadcrumbs = page.breadcrumbs;
  } else {
    breadcrumbs = routeFallback.breadcrumbs;
  }
  
  // Force empty breadcrumbs for Journey routes AND EA routes
  if (isProJourneyRoute || isFreemiumJourneyRoute || isEARoute || hideSecondaryNav) {
    breadcrumbs = [];
  }
  
  // For EA routes or when hideSecondaryNav is true, don't show tabs
  const shouldHideSecondaryNav = hideSecondaryNav || isEARoute;
  
  // Tabs (only show if not hiding secondary nav)
  const tabs = shouldHideSecondaryNav ? [] : (page.tabs?.length ? page.tabs : ['Tools', 'Documents', 'Messages']);
  
  const activeTab = activeFilter || 'Tools';
  
  console.log('🔵 AppShell - activeFilter:', activeFilter, 'activeTab:', activeTab, 'shouldHideSecondaryNav:', shouldHideSecondaryNav);

  if (error) {
    console.error('Error loading client profile:', error);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100/80 via-blue-50/40 to-slate-50">
      <AppHeader 
        plan={plan} 
        user={user} 
        workspaceTitle="Data Value Accelerator"
        onSearch={handleSearch}
        variant="glass"
        headline={headline}
        contextTitle={contextTitle}
        breadcrumbs={breadcrumbs}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        messageCount={messageCount}
        onOpenNotifications={() => setNotificationsOpen(true)}
        // For EA routes, use EA-specific upgrade path
        upgradeHref={isEARoute ? '/ea/upgrade' : '/freemium/upgrade'}
      />

      <NotificationsModal
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        onUnreadCountChange={setMessageCount}
      />
      
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-sm text-slate-600">Loading…</div>
        </div>
      ) : (
        <div>
          {children}
        </div>
      )}
    </div>
  );
}